import React, { useState, useEffect, useMemo } from "react";
import { Image } from "react-bootstrap";
import { Link } from "react-router-dom";

import { GetInvitationDetail200Response } from "@nestcoinco/hyper-api-gateway-api-client";

import "./SomeoneWantsToPlayYouModal.scss";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import BottomSheet from "../../BottomSheet/BottomSheet";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import ImageCloseIcon from "../../../images/close-icon.svg";
import ImageUntilTonight from "../../../images/you-have-until-tonight.svg";
import HyperCoin from "../../HyperCoin/HyperCoin";
import GetAppButton from "../../GetAppButton/GetAppButton";
import { ROUTES } from "../../../utils/constants";
import useAuth from "../../../hooks/useAuth";
import useDisclosure from "../../../hooks/useDisclosure";
import GetHyperOnTestFlightModal from "../../GetHyperOnTestFlightModal/GetHyperOnTestFlightModal";
import DownloadAndroidAppModal from "../../DownloadAndroidAppModal/DownloadAndroidAppModal";
import {
  GamerInfo,
  getGamerInfoFromShortCode,
} from "../../../api-clients/urlsClient";
import PersonIconAlt from "../../icons/PersonIconAlt";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import NotificationFlow from "../../notification-flow/NotificationFlow";
import useNotifications from "../../../hooks/useNotifications";
import { handleGetChallengeInvitationDetails } from "../../../api-clients/challengeInvitationsClient";
import DownloadAppPrompt from "../../DownloadAppPrompt/DownloadAppPrompt";
import useGetApp from "../../../hooks/useGetApp";
import { useTranslation } from "react-i18next";

const DEFAULT_STREAK_LENGTH = 5;
const dummyStreak: string[] = new Array(DEFAULT_STREAK_LENGTH).fill("");
enum GamesPageFlows {
  REFERRAL = "referral",
  CHALLENGE = "challenge",
  SHARE = "share",
}

function StreakItem({ result }: { result?: string }) {
  let bgClass: string;
  if (result?.toLowerCase() === "w") bgClass = "win";
  else if (result?.toLowerCase() === "l") bgClass = "loss";
  else if (result?.toLowerCase() === "t") bgClass = "tie";
  else bgClass = "blank";

  return (
    <div className={`streak-item ${bgClass}`}>
      <span>{result}</span>
    </div>
  );
}

interface SomeoneWantsToPlayYouModalProps extends GenericModalProps {
  shortCode?: string;
  challengeId?: string;
  referrerId?: string;
}

function SomeoneWantsToPlayYouModal({
  isOpen,
  onClose,
  shortCode,
  challengeId,
  referrerId,
}: SomeoneWantsToPlayYouModalProps) {
  const { isLoggedIn, userProfile } = useAuth();
  const { ctaText } = useGetApp();
  const notifications = useNotifications();
  const { t } = useTranslation("common");
  const [opposingGamerInfo, setOpposingGamerInfo] = useState<GamerInfo>();
  const [isLoading, setIsLoading] = useState(false);
  const [challengeInvitationDetails, setChallengeInvitationDetails] =
    useState<GetInvitationDetail200Response>();
  const {
    isOpen: isTestFlightInstructionsOpen,
    onOpen: openTestFlightInstructions,
    onClose: closeTestFlightInstructions,
  } = useDisclosure();
  const {
    isOpen: isAndroidAppDownloadOpen,
    onOpen: openDownloadAndroidApp,
    onClose: closeDownloadAndroidApp,
  } = useDisclosure();
  const {
    isOpen: isAppDownloadOpen,
    onOpen: openDownloadApp,
    onClose: closeDownloadApp,
  } = useDisclosure();

  let currentFlow: GamesPageFlows;
  if (challengeId) currentFlow = GamesPageFlows.CHALLENGE;
  if (referrerId) currentFlow = GamesPageFlows.REFERRAL;
  else currentFlow = GamesPageFlows.SHARE;

  const headingText = useMemo(
    () =>
      opposingGamerInfo?.username
        ? `${opposingGamerInfo?.username} wants to play you`
        : "Play your friend",
    [opposingGamerInfo?.username],
  );

  const opponentName = opposingGamerInfo?.username || "Your friend";
  const opponentStreak = opposingGamerInfo?.streak?.length
    ? opposingGamerInfo.streak
    : dummyStreak;

  const footerTopText = useMemo(
    () =>
      currentFlow === GamesPageFlows.REFERRAL ? (
        <p className="use-app-text d-flex align-items-center justify-content-center gap-1 mb-3">
          @{opposingGamerInfo?.username} {t("wants_you_to_try_out_hyper")}
        </p>
      ) : currentFlow === GamesPageFlows.SHARE ? (
        <p className="use-app-text d-flex align-items-center justify-content-center gap-1 mb-3">
          {t("use_or_get_the_app_to_play", { action: ctaText })}{" "}
          <strong>
            {opposingGamerInfo?.username ? "@" : ""}
            {opponentName}
          </strong>
        </p>
      ) : (
        <p className="use-app-text d-flex align-items-center justify-content-center gap-1 mb-3">
          {t("use_or_get_the_app_to_play", { action: ctaText })}{" "}
          <strong>
            {opposingGamerInfo?.username ? "@" : ""}
            {opponentName}
          </strong>{" "}
          {t("and_win")} <HyperCoin height={16} width={16} />{" "}
          {challengeInvitationDetails?.rewardAmount}
        </p>
      ),
    [
      t,
      ctaText,
      challengeInvitationDetails?.rewardAmount,
      currentFlow,
      opponentName,
      opposingGamerInfo?.username,
    ],
  );

  const handleDownloadApp = (openPlatformDialog: () => void) => {
    openPlatformDialog();
  };

  useEffect(() => {
    if (!shortCode) return;
    setIsLoading(true);
    getGamerInfoFromShortCode(shortCode)
      .then((res) => {
        if (!res) return;
        const { fullUrl, ...gamerInfo } = res;
        setOpposingGamerInfo(gamerInfo);
      })
      .finally(() => {
        setIsLoading(false);
      });

    if (challengeId) {
      handleGetChallengeInvitationDetails(challengeId).then((res) => {
        setChallengeInvitationDetails(res);
      });
    }
  }, [challengeId, shortCode]);

  useEffect(() => {
    const isSharer =
      userProfile && userProfile.username === opposingGamerInfo?.username;
    if (isSharer) onClose();
  }, [onClose, opposingGamerInfo?.username, userProfile]);

  return (
    <BottomSheet isOpen={isOpen}>
      <div className="someone-want-to-play-you-modal">
        <div className="pb-4">
          <div className="d-flex justify-content-end p-3">
            <GhostButton className="close-btn" onClick={onClose}>
              <Image alt="" src={ImageCloseIcon} />
            </GhostButton>
          </div>
          {isLoading && (
            <div className="spinner-wrapper">
              <CustomSpinner isPrimary />
            </div>
          )}
          {!isLoading && (
            <>
              <h2 className="heading">{headingText}</h2>
              <div>
                <div className="streak-info-wrapper opponent-streak">
                  <div className="avatar-wrapper">
                    <div>
                      {opposingGamerInfo?.avatar ? (
                        <Image
                          src={opposingGamerInfo.avatar}
                          className="avatar"
                          alt="avatar"
                        />
                      ) : (
                        <PersonIconAlt />
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="max-w-150 username">{opponentName}</h3>
                    <div className="win-streaks-wrapper">
                      <p className="win-streaks-text">Win streaks 🔥</p>
                      <div className="d-flex gap-1">
                        {opponentStreak?.map((result, i) => (
                          <StreakItem key={i} result={result} />
                        ))}
                        {new Array(
                          DEFAULT_STREAK_LENGTH - opponentStreak?.length || 0,
                        )
                          .fill("")
                          .map((v, i) => (
                            <StreakItem key={i} result={v} />
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex py-3 justify-content-center">
                  <Image src={ImageUntilTonight} />
                </div>
                <div className="d-flex justify-content-end">
                  <div className="streak-info-wrapper user-streak">
                    <div className="avatar-wrapper">
                      <div>
                        {userProfile?.avatar?.url ? (
                          <Image
                            src={userProfile.avatar.url}
                            className="avatar"
                            alt="avatar"
                          />
                        ) : (
                          <PersonIconAlt />
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="username">You</h3>
                      <div className="win-streaks-wrapper">
                        <p className="win-streaks-text">Win streaks 🔥</p>
                        <div className="d-flex gap-1">
                          {dummyStreak.map((result, i) => (
                            <StreakItem key={i} result={result} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {!isLoading && (
          <div className="footer px-3 py-4">
            {footerTopText}
            <GetAppButton
              openDownloadAndroidApp={() =>
                handleDownloadApp(openDownloadAndroidApp)
              }
              openTestFlightInstructions={() =>
                handleDownloadApp(openTestFlightInstructions)
              }
              style={{ background: "#6C53E8", color: "white" }}
            />
            {isLoggedIn ? (
              <GhostButton
                className="log-in-btn"
                onClick={notifications.openEnableNotificationsDialog}
              >
                Remind me later
              </GhostButton>
            ) : (
              <Link to={ROUTES.ROOT.LOG_IN} className="log-in-btn">
                Log into Hyper
              </Link>
            )}
          </div>
        )}
      </div>
      <GetHyperOnTestFlightModal
        isOpen={isTestFlightInstructionsOpen}
        onClose={closeTestFlightInstructions}
      />
      <DownloadAndroidAppModal
        isOpen={isAndroidAppDownloadOpen}
        onClose={closeDownloadAndroidApp}
      />
      <DownloadAppPrompt
        isOpen={isAppDownloadOpen}
        onClose={closeDownloadApp}
        onOpen={openDownloadApp}
        showNotificationsFlow={false}
      />
      <NotificationFlow handleGetApp={openDownloadApp} {...notifications} />
    </BottomSheet>
  );
}
export default SomeoneWantsToPlayYouModal;
